import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import CircleCluster from './CircleCluster';
import FloatingCards from './FloatingCards';
import VideoLaptop from './VideoLaptop';
import { track } from '../lib/analytics';
const Hero = ({ onConnect }) => {
  const [isHovering, setIsHovering] = useState(false);
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  // Background color transition: dark → white (0.5), hold until 0.7, fade to transparent (0.8)
  // Syncs with VideoLaptop shrink phase
  const backgroundColor = useTransform(
    scrollYProgress,
    [0, 0.4, 0.5, 0.7, 0.8],
    ['#122F26', '#122F26', '#F9F8F8', '#F9F8F8', 'rgba(254, 134, 48, 0.0)']
  );

  // Hero z-index management - stay high until animation completes
  const heroZIndex = useTransform(scrollYProgress, [0, 0.8, 0.85], [10, 10, 0]);

  // Text fade out early
  const textOpacity = useTransform(scrollYProgress, [0, 0.1, 0.2], [1, 1, 0]);

  return (
    <>
      <motion.section 
        ref={heroRef}
        id="home" 
        className="hero-wrap relative min-h-[650vh] flex items-start pt-32 justify-center overflow-hidden px-6"
        style={{ 
          backgroundColor,
          zIndex: heroZIndex,
          willChange: 'background-color',
        }}
      >
        {/* Background Circle Cluster - Bottom right, expands on scroll */}
        <CircleCluster scrollProgress={scrollYProgress} />

        {/* Floating Snapshot Cards */}
        <FloatingCards scrollProgress={scrollYProgress} />

        {/* Video Laptop (appears from circles, moves to center, shrinks) */}
        <VideoLaptop scrollProgress={scrollYProgress} />

        {/* Content */}
        <motion.div 
          className="relative z-10 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
          style={{ opacity: textOpacity }}
        >
          {/* Left: Text Content */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="space-y-6"
          >
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-sm font-medium tracking-wide uppercase"
              style={{ color: 'var(--accent-orange)' }}
            >
              Product Builder · AI Practitioner · Creator
            </motion.p>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight tracking-tight">
              <span style={{ color: 'var(--text-on-dark)' }}>I build products, </span>
              <br />
              <span className="gradient-text">content, and AI systems</span>
            </h1>

            <div
              className="space-y-2 max-w-lg"
              style={{ color: 'var(--text-on-dark)' }}
            >
              <p className="text-base md:text-lg font-semibold">
                AI Product Manager at Shiprocket.
              </p>
              <p className="text-sm md:text-base leading-relaxed" style={{ opacity: 0.75 }}>
                100K+ product users · 653+ GitHub contributions · 125K+ Instagram followers · 50M+ views, all in 2026.
              </p>
              <p className="text-sm md:text-base" style={{ opacity: 0.75 }}>
                Obsessed with human behaviour · IIT Delhi '25
              </p>
            </div>

            {/* CTA Buttons */}
            <motion.div 
              className="flex flex-wrap gap-4 pt-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <motion.a
                href="#projects"
                onClick={() => track('cta_click', { location: 'hero', target: 'projects' })}
                className="group px-8 py-4 rounded-full text-base font-semibold flex items-center space-x-2"
                style={{ background: 'var(--accent-green)', color: 'var(--bg-dark)' }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <span>Explore Projects</span>
                <motion.div
                  animate={{ x: [0, 4, 0] }}
                  transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
                >
                  <ArrowRight size={20} />
                </motion.div>
              </motion.a>

              <motion.button
                onClick={() => { track('cta_click', { location: 'hero', target: 'connect' }); onConnect(); }}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
                className="relative px-8 py-4 rounded-full text-base font-semibold overflow-hidden"
                style={{ 
                  background: 'transparent', 
                  color: isHovering ? 'white' : 'var(--accent-green)',
                  border: '2px solid var(--accent-green)',
                }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                {/* Spill animation background */}
                <motion.div
                  initial={{ scale: 0, x: '-50%', y: '-50%' }}
                  animate={isHovering ? { scale: 3 } : { scale: 0 }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute top-1/2 left-1/2 w-full h-full rounded-full"
                  style={{
                    background: 'radial-gradient(circle, var(--accent-orange) 0%, var(--accent-orange) 100%)',
                  }}
                />
                <span className="relative z-10">Let's Connect</span>
              </motion.button>
            </motion.div>
          </motion.div>

          {/* Right: Space for effects */}
          <div className="hidden lg:block" />
        </motion.div>
      </motion.section>
    </>
  );
};

export default Hero;
